// Text catalog for the Site Compliance service page.

import { type Locale } from "../config";
import { type PageMeta } from "../head";

type Block = { title: string; intro: string; items: string[] };

type SiteComplianceStrings = {
  meta: PageMeta;
  hero: { eyebrow: string; title: string; body: string; ctaPrimary: string; ctaSecondary: string };
  intro: { eyebrow: string; heading: string; body: string };
  activities: { eyebrow: string; heading: string; intro: string; blocks: Block[] };
  expert: { eyebrow: string; heading: string; lead: string; items: string[]; outro: string; cta: string };
  quote: { text: string; attrib: string };
  cta: { heading: string; body: string; call: string; write: string };
};

const da: SiteComplianceStrings = {
  meta: {
    title: "Site Compliance og kontraktørstyring | MI Support",
    description:
      "MI Support tilbyder Site Compliance-konsulenter, der sikrer lovlig og dokumenteret brug af kontraktører og underleverandører på store byggeprojekter.",
    ogImage: "https://www.misupport.dk/wp-content/uploads/MISupportIT_NTK2628_1350x900-1.jpg",
  },
  hero: {
    eyebrow: "Vores ydelser",
    title: "Site Compliance",
    body: "Site Compliance handler om at sikre lovlig og dokumenteret brug af underleverandører på store byggepladser. Fra adgangskontrol til dokumentation og rapportering. Vores konsulenter varetager de operationelle opgaver, så bygherren altid har fuldt overblik og kontrol",
    ctaPrimary: "Tag en uforpligtende snak",
    ctaSecondary: "Se alle ydelser",
  },
  intro: {
    eyebrow: "STYRING I STOR SKALA",
    heading: "Fuld kontrol over jeres kontraktørkæde",
    body: "Store byggeprojekter involverer ofte hundredevis af medarbejdere på tværs af flere underleverandørled. Det stiller høje krav til dokumentation, kontrol og risikostyring, både i forhold til dansk lovgivning, overenskomster og bygherrens egne krav. Vores Site Compliance-konsulenter understøtter bygherrens compliance-funktion med kvalificeret, trænet personale, der efter en kort introduktion til kundens processer selvstændigt kan udføre de daglige opgaver. Bygherren bevarer det fulde ejerskab af governance, risikovurderinger og beslutninger om sanktioner og afvigelser.",

  },
  activities: {
    eyebrow: "Hvad indebærer Site Compliance?",
    heading: "Operationelle opgaver på tværs af kontraktørkæden",
    intro:
      "Vores konsulenter dækker hele forløbet, fra onboarding af nye underleverandørs til løbende dokumentation, opfølgning og rapportering, inden for kundens egne systemer og procedurer.",

    blocks: [
      {
        title: "Onboarding og adgangskontrol",
        intro:
          "Vi sikrer, at underleverandørs og deres medarbejdere er lovligt registreret og godkendt, før de får adgang til pladsen.",

        items: [
          "Validering af kontraktørers og underleverandørers virksomhedsregistrering (CVR) og forretningsgrundlag",
          "Kontrol af udenlandske virksomheders registrering i RUT (Registret for Udenlandske Tjenesteydere)",
          "Verificering af løn- og ansættelsesvilkår og kontrol for social dumping",
          "Kontrol af overenskomstdækning eller tilsvarende vilkår",
          "Verificering af udenlandske medarbejderes arbejds- og opholdstilladelser",
          "Administration af adgangskort og adgangsrettigheder",
          "Daglig drift af check-in og adgangskontrol på pladsen",
        ],
      },
      {
        title: "Dokumentation og datahåndtering",
        intro:
          "Vi holder styr på kontraktørdata og dokumentation i kundens systemer og sikrer, at alt er opdateret og gyldigt.",
        items: [
          "Indsamling, registrering og løbende opdatering af kontraktørdata i kundens systemer",
          "Overvågning af gyldighedsperioder på dokumentation",
          "Opfølgning på manglende, ufuldstændige eller udløbne dokumenter (arbejdstilladelser, forsikringer, ansættelseskontrakter mv.)",
          "Kontrol af A1-dokumentation og RUT-registrering",
          "Håndtering af persondata i overensstemmelse med GDPR og kundens databehandleraftale",
        ],
      },
      {
        title: "Compliance-opfølgning",
        intro:
          "Vi understøtter kundens kontrolplan med systematiske stikprøver, audits og opfølgning på afvigelser.",
        items: [
          "Understøttelse af stikprøvekontroller i henhold til kundens kontrolplan",
          "Klargøring af dokumentation til eksterne audits",
          "Identificering af afvigelser og eskalering efter kundens procedurer",
          "Opfølgning på korrigerende handlinger",
          "Registrering af gennemførte introduktioner (\"inductions\")",
        ],
      },
      {
        title: "Rapportering",
        intro:
          "Vi leverer rapporter og dashboards, så ledelsen løbende har et retvisende overblik over compliance-status.",
        items: [
          "Udarbejdelse af faste rapporter og datauddrag i aftalt format",
          "Vedligeholdelse af dashboards og datagrundlag",
          "Løbende rapportering af status og afvigelser til kunden",
        ],
      },
    ],
  },
  expert: {
    eyebrow: "Erfarne compliance-konsulenter",
    heading: "En fast tilknyttet compliance-funktion",
    lead: "Vores konsulenter kender de danske regler og de praktiske arbejdsgange på store byggepladser. De arbejder inden for kundens egne systemer og processer og kan tage ansvar for driften fra dag ét.",
    items: [
      "Solid erfaring med dansk arbejdsret og arbejdsmiljølovgivning",
      "Indgående kendskab til RUT, A1-dokumentation og udenlandsk arbejdskraft",
      "Erfaring med GDPR og håndtering af persondata i for underleveradøre",
      "Kapacitet til at bemande flere byggepladser samtidigt",
      "Flersprogede konsulenter (dansk og engelsk som minimum)",
      "Erfaring med digitale compliance- og registreringsværktøjer",
    ],
    outro:
      "MI Support kan skalere bemandingen op og ned i takt med projektets omfang. Konsulenterne arbejder inden for kundens egne systemer og processer, mens kunden bevarer det fulde ejerskab af compliance-beslutninger.",
    cta: "Tag en uforpligtende snak",
  },
  quote: {
    text: "\"Med mange underleverandøre er det den daglige struktur og den tætte opfølgning, der afgør, om compliance bliver til en reel sikkerhed eller blot et stykke papir. Det handler om at være til stede og sikre opfølgning.\"",
    attrib: "Site Compliance-konsulent, MI Support",
  },
  cta: {
    heading: "Skal vi styrke jeres Site Compliance?",
    body: "Ønsker I fuld kontrol over jeres leverandøre med dokumenteret compliance på tværs af byggepladser? Lad os tage en snak om, hvordan MI Support kan understøtte jeres compliance-funktion.",
    call: "Ring +45 5555 1212",
    write: "Skriv til os",
  },
};

const en: SiteComplianceStrings = {
  meta: {
    title: "Site Compliance - Subcontractor management and documentation control on site",
    description:
      "MI Support provides Site Compliance consultants who ensure lawful and documented use of subcontractors on large construction projects.",
    ogImage: "https://www.misupport.dk/wp-content/uploads/MISupportIT_NTK2628_1350x900-1.jpg",
  },
  hero: {
    eyebrow: "Our services",
    title: "Site Compliance",
    body: "Site Compliance is about ensuring the lawful and documented use of subcontractors on large construction sites. From access control to documentation and reporting. Our consultants handle the operational tasks so the client always has full overview and control.",
    ctaPrimary: "Book a no-obligation talk",
    ctaSecondary: "See all services",
  },
  intro: {
    eyebrow: "MANAGEMENT AT SCALE",
    heading: "Full control of your subcontractor chain",
    body: "Large construction projects often involve hundreds of workers across several tiers of subcontractors. That places high demands on documentation, control and risk management — both in relation to Danish law, collective agreements and the client's own requirements. Our Site Compliance consultants support the client's compliance function with qualified, trained personnel who, after a short introduction to the customer's processes, can independently carry out the daily tasks. The client retains full ownership of governance, risk assessments and decisions on sanctions and deviations.",
  },
  activities: {
    eyebrow: "What does Site Compliance involve?",
    heading: "Operational tasks across the subcontractor chain",
    intro:
      "Our consultants cover the entire process, from onboarding of new subcontractors to ongoing documentation, follow-up and reporting, within the customer's own systems and procedures.",
    blocks: [
      {
        title: "Onboarding and access control",
        intro:
          "We ensure that subcontractors and their employees are lawfully registered and approved before they gain access to the site.",
        items: [
          "Validation of contractors' and subcontractors' company registration (CVR) and business basis",
          "Verification of foreign companies' registration in RUT (Register of Foreign Service Providers)",
          "Verification of pay and employment conditions and checks for social dumping",
          "Verification of collective bargaining coverage or equivalent conditions",
          "Verification of foreign employees' work and residence permits",
          "Administration of access cards and access rights",
          "Daily operation of check-in and access control on site",
        ],
      },
      {
        title: "Documentation and data handling",
        intro:
          "We keep track of contractor data and documentation in the customer's systems and ensure that everything is up to date and valid.",
        items: [
          "Collection, registration and ongoing updates of contractor data in the customer's systems",
          "Monitoring of documentation validity periods",
          "Follow-up on missing, incomplete or expired documents (work permits, insurance, employment contracts, etc.)",
          "Verification of A1 documentation and RUT registration",
          "Handling personal data in accordance with GDPR and the customer's data processing agreement",
        ],
      },
      {
        title: "Compliance follow-up",
        intro:
          "We support the customer's control plan with systematic spot checks, audits and follow-up on deviations.",
        items: [
          "Support for spot checks in accordance with the customer's control plan",
          "Preparation of documentation for external audits",
          "Identification of deviations and escalation according to the customer's procedures",
          "Follow-up on corrective actions",
          "Registration of completed inductions",
        ],
      },
      {
        title: "Reporting",
        intro:
          "We deliver reports and dashboards so management continuously has an accurate overview of compliance status.",
        items: [
          "Preparation of standard reports and data extracts in agreed format",
          "Maintenance of dashboards and data foundation",
          "Ongoing reporting of status and deviations to the customer",
        ],
      },
    ],
  },
  expert: {
    eyebrow: "Experienced compliance consultants",
    heading: "A dedicated compliance function",
    lead: "Our consultants know the Danish rules and the practical workflows on large construction sites. They work within the customer's own systems and processes and can take responsibility for operations from day one.",
    items: [
      "Solid experience with Danish labour law and occupational health and safety legislation",
      "In-depth knowledge of RUT, A1 documentation and foreign labour",
      "Experience with GDPR and handling personal data for subcontractors",
      "Capacity to staff several construction sites simultaneously",
      "Multilingual consultants (Danish and English as a minimum)",
      "Experience with digital compliance and registration tools",
    ],
    outro:
      "MI Support can scale staffing up and down in line with the project's scope. The consultants work within the customer's own systems and processes, while the customer retains full ownership of compliance decisions.",
    cta: "Book a no-obligation talk",
  },
  quote: {
    text: "With many subcontractors, it is the daily structure and close follow-up that decide whether compliance becomes real security or just a piece of paper. It's about being present and ensuring follow-up.",
    attrib: "Site Compliance consultant, MI Support",
  },
  cta: {
    heading: "Shall we strengthen your Site Compliance?",
    body: "Do you want full control of your suppliers with documented compliance across construction sites? Let's talk about how MI Support can support your compliance function.",
    call: "Call +45 5555 1212",
    write: "Write to us",
  },
};

const de: SiteComplianceStrings = {
  meta: {
    title: "Site Compliance - Nachunternehmermanagement und Dokumentationskontrolle auf der Baustelle",
    description:
      "MI Support stellt Site-Compliance-Berater bereit, die den rechtmäßigen und dokumentierten Einsatz von Nachunternehmern auf großen Bauprojekten sicherstellen.",
    ogImage: "https://www.misupport.dk/wp-content/uploads/MISupportIT_NTK2628_1350x900-1.jpg",
  },
  hero: {
    eyebrow: "Unsere Leistungen",
    title: "Site Compliance",
    body: "Site Compliance stellt den rechtmäßigen und dokumentierten Einsatz von Nachunternehmern auf großen Baustellen sicher – von der Zugangskontrolle über die Dokumentation bis zum Reporting. Unsere Berater übernehmen die operativen Aufgaben, sodass der Bauherr jederzeit den vollen Überblick und die Kontrolle behält.",
    ctaPrimary: "Unverbindliches Gespräch vereinbaren",
    ctaSecondary: "Alle Leistungen ansehen",
  },
  intro: {
    eyebrow: "STEUERUNG IN GROSSEM MASSSTAB",
    heading: "Volle Kontrolle über Ihre Nachunternehmerkette",
    body: "Große Bauprojekte umfassen häufig hunderte Mitarbeitende über mehrere Nachunternehmerebenen hinweg. Das stellt hohe Anforderungen an Dokumentation, Kontrolle und Risikomanagement – sowohl in Bezug auf dänisches Recht und Tarifverträge als auch auf die Anforderungen des Bauherrn. Unsere Site-Compliance-Berater unterstützen die Compliance-Funktion des Bauherrn mit qualifiziertem, geschultem Personal, das nach einer kurzen Einführung in die Kundenprozesse die täglichen Aufgaben eigenständig übernimmt. Der Bauherr behält die volle Verantwortung für Governance, Risikobewertungen und Entscheidungen über Sanktionen und Abweichungen.",
  },
  activities: {
    eyebrow: "Was umfasst Site Compliance?",
    heading: "Operative Aufgaben entlang der Nachunternehmerkette",
    intro:
      "Unsere Berater decken den gesamten Prozess ab – vom Onboarding neuer Nachunternehmer bis zur laufenden Dokumentation, Nachverfolgung und Berichterstattung – innerhalb der Systeme und Verfahren des Kunden.",
    blocks: [
      {
        title: "Onboarding und Zugangskontrolle",
        intro:
          "Wir stellen sicher, dass Nachunternehmer und ihre Mitarbeitenden rechtmäßig registriert und freigegeben sind, bevor sie Zugang zur Baustelle erhalten.",
        items: [
          "Validierung der Unternehmensregistrierung (CVR) und der Geschäftsgrundlage von Auftragnehmern und Nachunternehmern",
          "Prüfung der Registrierung ausländischer Unternehmen im RUT (Register ausländischer Dienstleister)",
          "Überprüfung der Lohn- und Beschäftigungsbedingungen und Kontrolle auf Sozialdumping",
          "Prüfung der Tarifbindung oder gleichwertiger Bedingungen",
          "Überprüfung der Arbeits- und Aufenthaltserlaubnisse ausländischer Mitarbeitender",
          "Verwaltung von Zugangskarten und Zugangsrechten",
          "Täglicher Betrieb von Check-in und Zugangskontrolle auf der Baustelle",
        ],
      },
      {
        title: "Dokumentation und Datenverwaltung",
        intro:
          "Wir pflegen die Nachunternehmerdaten und Dokumentation in den Systemen des Kunden und stellen sicher, dass alles aktuell und gültig ist.",
        items: [
          "Erfassung, Registrierung und laufende Aktualisierung von Nachunternehmerdaten in den Kundensystemen",
          "Überwachung der Gültigkeitszeiträume von Dokumenten",
          "Nachverfolgung fehlender, unvollständiger oder abgelaufener Dokumente (Arbeitserlaubnisse, Versicherungen, Arbeitsverträge usw.)",
          "Prüfung von A1-Bescheinigungen und RUT-Registrierungen",
          "Umgang mit personenbezogenen Daten gemäß DSGVO und der Auftragsverarbeitungsvereinbarung des Kunden",
        ],
      },
      {
        title: "Compliance-Nachverfolgung",
        intro:
          "Wir unterstützen den Kontrollplan des Kunden mit systematischen Stichproben, Audits und der Nachverfolgung von Abweichungen.",
        items: [
          "Unterstützung bei Stichprobenkontrollen gemäß Kontrollplan des Kunden",
          "Aufbereitung von Dokumentation für externe Audits",
          "Identifizierung von Abweichungen und Eskalation nach den Verfahren des Kunden",
          "Nachverfolgung von Korrekturmaßnahmen",
          "Registrierung durchgeführter Einweisungen (\"Inductions\")",
        ],
      },
      {
        title: "Berichterstattung",
        intro:
          "Wir liefern Berichte und Dashboards, sodass das Management jederzeit einen verlässlichen Überblick über den Compliance-Status hat.",
        items: [
          "Erstellung fester Berichte und Datenauszüge im vereinbarten Format",
          "Pflege von Dashboards und Datengrundlage",
          "Laufende Berichterstattung über Status und Abweichungen an den Kunden",
        ],
      },
    ],
  },
  expert: {
    eyebrow: "Erfahrene Compliance-Berater",
    heading: "Eine fest eingebundene Compliance-Funktion",
    lead: "Unsere Berater kennen die dänischen Vorschriften und die praktischen Abläufe auf großen Baustellen. Sie arbeiten in den Systemen und Prozessen des Kunden und können vom ersten Tag an Verantwortung für den Betrieb übernehmen.",
    items: [
      "Fundierte Erfahrung mit dänischem Arbeitsrecht und Arbeitsschutzgesetzgebung",
      "Umfassende Kenntnisse zu RUT, A1-Bescheinigungen und ausländischen Arbeitskräften",
      "Erfahrung mit DSGVO und der Verarbeitung personenbezogener Daten von Nachunternehmern",
      "Kapazität für die Besetzung mehrerer Baustellen gleichzeitig",
      "Mehrsprachige Berater (mindestens Dänisch und Englisch)",
      "Erfahrung mit digitalen Compliance- und Registrierungstools",
    ],
    outro:
      "MI Support kann die Personalstärke flexibel an den Projektumfang anpassen. Die Berater arbeiten in den Systemen und Prozessen des Kunden, während der Kunde die volle Verantwortung für Compliance-Entscheidungen behält.",
    cta: "Unverbindliches Gespräch vereinbaren",
  },
  quote: {
    text: "Bei vielen Nachunternehmern entscheiden die tägliche Struktur und die enge Nachverfolgung darüber, ob Compliance zu echter Sicherheit oder nur zu einem Stück Papier wird. Es geht darum, präsent zu sein und die Nachverfolgung sicherzustellen.",
    attrib: "Site-Compliance-Berater, MI Support",
  },
  cta: {
    heading: "Sollen wir Ihre Site Compliance stärken?",
    body: "Möchten Sie die volle Kontrolle über Ihre Lieferanten mit dokumentierter Compliance über alle Baustellen hinweg? Lassen Sie uns darüber sprechen, wie MI Support Ihre Compliance-Funktion unterstützen kann.",
    call: "Anrufen +45 5555 1212",
    write: "Schreiben Sie uns",
  },
};

export const siteComplianceStrings: Record<Locale, SiteComplianceStrings> = {
  da,
  en,
  de,
};